import { useState, useMemo, useContext } from "react";
import { Button, Table, Form, InputGroup, Pagination, Col } from "react-bootstrap";
import { Search, Edit3, Trash2, Plus } from "lucide-react"; // Or use react-icons equivalents
import { ToastContainer, toast, Zoom } from 'react-toastify';
import { Dimcontext } from "../../context/pagecontext";
import { Editnod } from "./editnod";
import { Docdel } from "./docdel";
import { Adddoc } from "./adddoc";
import '../css/document/document.css';
import { motion, AnimatePresence } from "framer-motion";
import { Datacontext } from "../../context/dataContext";

export const Document = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // State for selectable pagination
  
  const [modalState, setModalState] = useState({ type: null, id: null });
  const { setDim } = useContext(Dimcontext);
  const { docs, fetchDocs } = useContext(Datacontext)


  // Search & Pagination Logic
  const filteredDocs = useMemo(() => {
    return docs.filter(d => d.doc_Name.toLowerCase().includes(searchTerm.toLowerCase().trim()));
  }, [docs, searchTerm]);

  const totalPages = Math.ceil(filteredDocs.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDocs.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleRowsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to page 1 when changing limit
  };

  const showToast = (msg) => 
    toast.warn(msg, {
      position: "bottom-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "dark",
      transition: Zoom,
    });

  const openModal = (type, id = null) => {
    setModalState({ type, id });
    setDim(true);
  };

  const closeModal = () => {
    setModalState({ type: null, id: null });
    setDim(false);
  };

  return (
    <div className="document-container p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="page-title">Nature of Documents</h3>
        <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center mb-4">
  <Col xs={12} md={5} lg={4}>
    <InputGroup className="search-bar-container">
      <InputGroup.Text className="bg-white border-end-0">
        <Search size={18} className="text-muted" />
      </InputGroup.Text>
      <Form.Control 
        className="border-start-0 ps-0"
        placeholder="Search documents..." 
        value={searchTerm} 
        onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
      />
    </InputGroup>
  </Col>
  
  <Button className="btn-add d-flex align-items-center justify-content-center gap-2" onClick={() => openModal('add')}>
    <Plus size={18} /> <span>Add Document</span>
  </Button>
</div>
      </div>

      <div className="table-card shadow-sm">
        <Table hover striped responsive className="m-0">
          <thead>
            <tr>
              <th className="text-center" width="100">No.</th>
              <th>Document Name</th>
              <th className="text-center" width="150">Action</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((d, index) => (
              <tr key={d.id}>
                <td className="text-center">{indexOfFirstItem + index + 1}</td>
                <td className="text-uppercase fw-semibold">{d.doc_Name}</td>
                <td>
                  <div className="d-flex justify-content-center gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => openModal('edit', d.id)}><Edit3 size={16}/></Button>
                    <Button variant="outline-danger" size="sm" onClick={() => openModal('delete', d.id)}><Trash2 size={16}/></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* --- Bottom Select Pagination Bar --- */}
        <div className="pagination-footer d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top bg-light gap-3">
    
    {/* Left: Rows Per Page */}
    <div className="d-flex align-items-center gap-2">
        <span className="text-muted small">Rows per page:</span>
        <Form.Select 
            size="sm" 
            style={{ width: '80px', borderRadius: '6px' }}
            value={itemsPerPage} 
            onChange={handleRowsPerPageChange}
        >
            {[5, 10, 20, 50].map(v => (
                <option key={v} value={v}>{v}</option>
            ))}
        </Form.Select>
    </div>

    {/* Middle: Data Summary */}
    <div className="text-muted small fw-medium">
        {filteredDocs.length > 0 ? (
            <>
                Showing <b>{indexOfFirstItem + 1}</b> to <b>{Math.min(indexOfLastItem, filteredDocs.length)}</b> of <b>{filteredDocs.length}</b> records
            </>
        ) : (
            "No records found"
        )}
    </div>

    {/* Right: Navigation */}
    <Pagination className="mb-0 custom-pagination shadow-sm">
        <Pagination.First 
            disabled={currentPage === 1} 
            onClick={() => handlePageChange(1)} 
        />
        <Pagination.Prev 
            disabled={currentPage === 1} 
            onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
        />

        <Pagination.Item active className="px-2">{currentPage}</Pagination.Item>

        <Pagination.Next 
            disabled={currentPage === totalPages || totalPages === 0} 
            onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
        />
        <Pagination.Last 
            disabled={currentPage === totalPages || totalPages === 0} 
            onClick={() => handlePageChange(totalPages)} 
        />
    </Pagination>
</div>
      </div>

      <ToastContainer />

      <AnimatePresence mode="wait">
  {modalState.type && (
    /* Add a motion div for the backdrop itself to prevent flickering */
    <motion.div 
      key="modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="custom-modal-backdrop"
      // Prevent clicks inside the card from closing the backdrop accidentally
      onClick={(e) => e.target.classList.contains('custom-modal-backdrop') && closeModal()}
    >
      {modalState.type === 'add' && (
        <motion.div
          key="add-modal"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          className="modal-content-wrapper"
        >
          {/* Note: Removed showToast prop if using global ToastContainer, 
              but kept it for your custom logic */}
          <Adddoc set_add={closeModal} showToast={showToast} docg={fetchDocs} />
        </motion.div>
      )}

      {modalState.type === 'edit' && (
        <motion.div
          key="edit-modal"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          className="modal-content-wrapper"
        >
          <Editnod set_edit={closeModal} showToast={showToast} id={modalState.id} docg={fetchDocs} />
        </motion.div>
      )}

      {modalState.type === 'delete' && (
        <motion.div
          key="delete-modal"
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 100 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="modal-content-wrapper"
        >
          <Docdel set_del={closeModal} showToast={showToast} id={modalState.id} docg={fetchDocs} />
        </motion.div>
      )}
    </motion.div>
  )}
</AnimatePresence>
      
    </div>
  );
};